import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useOnError } from '@services/utils/onError'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// Hand-written until the OpenAPI types are regenerated.

/**
 * A minor named in the tenant contract as podnájomca. No account and no
 * signature of their own: the representative signs for them.
 */
export type ContractMinor = {
  /** Missing for a minor not saved yet. */
  id?: string
  degree?: string | null
  first_name: string
  last_name: string
  /** yyyy-MM-dd */
  birth_date: string
  /** Rodné číslo; required unless the country is foreign. */
  id_number: string
  /** Required when the country is foreign. */
  passport_number: string
  street: string
  address_number: string
  address_orientation_number: string
  zip: string
  city: string
  country: string
  nationality: string
  /** User id of the main tenant or a roommate. */
  representative: string
}

export type ContractMinorSigner = {
  id: string
  name: string
  /** False when the signer is a minor themselves. */
  can_represent: boolean
}

type MinorsResponse = {
  minors: ContractMinor[]
  signers: ContractMinorSigner[]
}

const url = `${SNA_PREFIX}contracts/offers/minors`
const queryKey = (offerId: string) => ['contract-minors', offerId]

export function useContractMinors(offerId: string, enabled = true) {
  return useQuery({
    queryKey: queryKey(offerId),
    queryFn: async () => {
      const res = await apiClient.get<MinorsResponse>(url, {
        params: { offer_id: offerId }
      })
      return res.data
    },
    enabled
  })
}

/** Replaces the whole list; minors left out are removed. */
export function useSaveContractMinors(offerId: string) {
  const queryClient = useQueryClient()
  const onError = useOnError()

  return useMutation({
    mutationFn: async (minors: ContractMinor[]) => {
      const res = await apiClient.put<MinorsResponse>(url, {
        offer_id: offerId,
        minors
      })
      return res.data
    },
    onSuccess: (data) => queryClient.setQueryData(queryKey(offerId), data),
    onError
  })
}
